import { Check, Euro, Pencil } from "lucide-react";
import { CATEGORY_COLORS, de, type Item } from "@ainkauf/core";
import { kategorieZeichen } from "@/lib/category-icons";

function mengeText(item: Item): string | null {
  if (item.quantity === null) return null;
  const zahl = Number.isInteger(item.quantity) ? String(item.quantity) : item.quantity.toLocaleString("de-DE");
  return item.unit ? `${zahl} ${item.unit}` : zahl;
}

/**
 * Eine Zeile der Liste, als eigene weiße Karte.
 *
 * Links steht das Zeichen der Warengruppe in ihrer Farbe, in einem Kreis, der
 * beim Abhaken zum Haken wird. Zwei Grüntöne sehen aus einem Meter Entfernung
 * gleich aus, ein Apfel und ein Brot nicht: Farbe und Zeichen zusammen machen
 * aus einer Tabelle eine Liste, die man im Vorbeigehen überfliegt. Die
 * Farbkante links bindet die Zeile an ihren Gang.
 *
 * Die ganze Zeile hakt ab, der Stift öffnet die Bearbeitung. Nichts hängt an
 * Hover oder langem Drücken (Regel 8).
 *
 * Seit dem 17.10.2026 kompakt: 48 statt 62 Pixel hoch. Daniel sah auf dem
 * Telefon nur fünf Artikel auf einmal und wollte es dichter, wie in Microsoft
 * To Do. 48 Pixel bleiben über der Grenze von 44 für den Daumen.
 *
 * Das Euro-Zeichen rechts (seit 17.10.2026) markiert "nur im Angebot kaufen":
 * kein dringender Artikel. Aus ist es blass, an steht es auf Honig-Grund.
 */
export function ItemRow({
  item,
  onToggle,
  onEdit,
  onToggleSale,
  takt = 0,
  checkedByName = null,
}: {
  item: Item;
  onToggle: (item: Item) => void;
  onEdit?: (item: Item) => void;
  onToggleSale?: (item: Item) => void;
  /** Versatz des Auftritts in Millisekunden, damit die Liste nacheinander erscheint. */
  takt?: number;
  /** Wer abgehakt hat, wenn es jemand anders war. Sonst steht da nichts. */
  checkedByName?: string | null;
}) {
  const menge = mengeText(item);
  const angebot = item.only_on_sale === true;
  const farbe = item.category ? `var(--food-${CATEGORY_COLORS[item.category]})` : "var(--ink-3)";

  return (
    <li
      className="karte kante auftritt flex items-center gap-1"
      style={{ ["--kante-farbe" as string]: item.is_checked ? "var(--paper-2)" : farbe, ["--takt" as string]: `${takt}ms` }}
    >
      <button
        type="button"
        onClick={() => {
          onToggle(item);
        }}
        aria-pressed={item.is_checked}
        data-testid="item-row"
        data-name={item.name}
        data-checked={item.is_checked}
        className="flex min-h-[48px] min-w-0 flex-1 items-center gap-2.5 py-1 pl-3 pr-1 text-left transition-transform duration-150 active:scale-[0.99]"
      >
        {/*
          Ein Kreis, zwei Zustände: offen trägt er das Zeichen seiner
          Warengruppe auf getöntem Grund, abgehakt füllt er sich ganz in der
          Farbe und zeigt den Haken, mit einem kleinen Nachfedern. Das Abhaken
          ist die häufigste Handlung dieser App, es darf sich gut anfühlen.
        */}
        <span
          aria-hidden
          className="relative inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 [transition-timing-function:var(--ease-spring)]"
          style={{
            borderColor: item.is_checked ? farbe : `color-mix(in oklab, ${farbe} 42%, transparent)`,
            background: item.is_checked ? farbe : `color-mix(in oklab, ${farbe} 13%, #fff)`,
            transform: item.is_checked ? "scale(1.05)" : "scale(1)",
          }}
        >
          <span
            className="absolute inset-0 grid place-items-center transition-opacity duration-150"
            style={{ color: farbe, opacity: item.is_checked ? 0 : 1 }}
          >
            {kategorieZeichen(item.category, 15)}
          </span>
          <span
            className="absolute inset-0 grid place-items-center text-white transition-opacity duration-150"
            style={{ opacity: item.is_checked ? 1 : 0 }}
          >
            <Check size={17} strokeWidth={3} />
          </span>
        </span>

        <span className="min-w-0 flex-1">
          <span className={`block truncate font-medium ${item.is_checked ? "text-ink-3 line-through" : "text-ink"}`}>{item.name}</span>
          {item.note !== null && <span className="block truncate text-[13px] leading-tight text-ink-2">{item.note}</span>}
          {/* Zu zweit im Laden zählt, wer etwas im Wagen hat. Der eigene
              Haken braucht keinen Namen, der fremde schon. */}
          {item.is_checked && checkedByName !== null && (
            <span className="block truncate text-sm text-ink-2" data-testid="checked-by">
              {de.items.checkedBy(checkedByName)}
            </span>
          )}
        </span>

        {menge !== null && (
          <span
            className="num shrink-0 rounded-full px-2 py-0.5 text-[13px] font-bold"
            style={{
              background: item.is_checked ? "var(--paper-2)" : `color-mix(in oklab, ${farbe} 15%, #fff)`,
              color: item.is_checked ? "var(--ink-3)" : `color-mix(in oklab, ${farbe} 72%, var(--ink))`,
            }}
          >
            {menge}
          </span>
        )}
      </button>

      {onToggleSale && !item.is_checked && (
        <button
          type="button"
          onClick={() => {
            onToggleSale(item);
          }}
          aria-pressed={angebot}
          aria-label={de.items.onlyOnSaleToggle(item.name, angebot)}
          title={de.items.onlyOnSale}
          data-testid="item-sale"
          data-name={item.name}
          data-on={angebot}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90"
        >
          <span
            aria-hidden
            className={`inline-grid size-7 place-items-center rounded-full transition-colors duration-150 ${
              angebot ? "bg-honey text-ink" : "text-ink-3 opacity-60"
            }`}
          >
            <Euro size={15} strokeWidth={angebot ? 2.5 : 1.75} />
          </span>
        </button>
      )}

      {onEdit && (
        <button
          type="button"
          onClick={() => {
            onEdit(item);
          }}
          aria-label={`${de.items.edit}: ${item.name}`}
          data-testid="item-edit"
          data-name={item.name}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
        >
          <Pencil size={16} strokeWidth={1.75} aria-hidden />
        </button>
      )}
    </li>
  );
}
